import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import { fetchBtcNasdaq } from '../utils/btcNasdaqApi';
import {
  fmtSignedCorr,
  pickCorr,
  regime,
  situationAtPoint,
} from '../utils/btcNasdaqSignal';
import BtcNasdaqHelp from './BtcNasdaqHelp';
import './BondDashboard.css';
import './BtcNasdaqPanel.css';
import './StockBondReturnsPanel.css';

const RANGE_OPTIONS = ['6M', '1Y', '2Y', '5Y', 'All'];
const DEFAULT_RANGE = '5Y';
const BTC_SOURCE_OPTIONS = [
  { id: 'etp', label: 'ETP' },
  { id: 'auto', label: 'Auto' },
  { id: 'kraken', label: 'Kraken' },
];
const CORR_MIN = -1;
const CORR_MAX = 1;
const SCALE_MIN_WIDTH = 72;

const COLOR_BTC = '#E07020';
const COLOR_NASDAQ = '#1F4E8C';
const CORR_COLORS = { 30: '#A16D17', 60: '#3A17A1', 90: '#1776A1' };

function toChartData(bars) {
  return (bars || []).map((b) => ({ time: b.time, value: Number(b.value) }));
}

function crosshairTimeString(time) {
  if (time == null) return null;
  if (typeof time === 'string') return time.slice(0, 10);
  if (typeof time === 'object' && time.year != null) {
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${time.year}-${m}-${d}`;
  }
  return String(time).slice(0, 10);
}

function formatChartDate(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${d}, ${y}`;
}

function BtcNasdaqPanel() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const payloadRef = useRef(null);

  const [range, setRange] = useState(DEFAULT_RANGE);
  const [btcSource, setBtcSource] = useState('etp');
  const [btcMethod, setBtcMethod] = useState('aligned_21utc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const situation = payload?.situation;
  const bars = payload?.bars;
  const latest = payload?.latest;

  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBtcNasdaq({ range, btcMethod, btcSource });
      setPayload(data);
    } catch (err) {
      setError(err.message || 'Failed to load BTC vs Nasdaq');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [range, btcMethod, btcSource]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!payload?.bars || !chartContainerRef.current) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      leftPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.2, bottom: 0.14 },
      },
      rightPriceScale: {
        visible: true,
        borderColor: '#E3D9C6',
        minimumWidth: SCALE_MIN_WIDTH,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 360,
    });

    const btcSeries = chart.addSeries(LineSeries, {
      color: COLOR_BTC,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: true,
      title: 'BTC',
    });
    const nasdaqSeries = chart.addSeries(LineSeries, {
      color: COLOR_NASDAQ,
      lineWidth: 2,
      priceScaleId: 'left',
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'Nasdaq',
    });

    const corrSeries = {};
    for (const w of [30, 60, 90]) {
      corrSeries[w] = chart.addSeries(LineSeries, {
        color: CORR_COLORS[w],
        lineWidth: 1.5,
        lineStyle: 1,
        priceScaleId: 'right',
        priceLineVisible: false,
        lastValueVisible: false,
        title: `${w}d`,
      });
    }
    const spearmanSeries = chart.addSeries(LineSeries, {
      color: '#6A4C93',
      lineWidth: 1.5,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
      title: 'Sp90',
    });
    const zeroSeries = chart.addSeries(LineSeries, {
      color: '#4A5672',
      lineWidth: 1,
      lineStyle: 2,
      priceScaleId: 'right',
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const { bars: b } = payload;
    btcSeries.setData(toChartData(b.btc_indexed));
    nasdaqSeries.setData(toChartData(b.nasdaq_indexed));
    corrSeries[30].setData(toChartData(b.corr30));
    corrSeries[60].setData(toChartData(b.corr60));
    corrSeries[90].setData(toChartData(b.corr90));
    spearmanSeries.setData(toChartData(b.spearman90));
    const zeroBase = b.corr90?.length ? b.corr90 : b.corr30;
    if (zeroBase?.length) {
      zeroSeries.setData(zeroBase.map((p) => ({ time: p.time, value: 0 })));
    }

    try {
      chart.priceScale('right').applyOptions({ autoScale: false, scaleMargins: { top: 0.08, bottom: 0.08 } });
      chart.priceScale('right').setVisibleRange({ from: CORR_MIN, to: CORR_MAX });
    } catch {
      /* empty */
    }
    chart.timeScale().fitContent();

    const onCrosshairMove = (param) => {
      if (!param.time || param.point == null) {
        setHoverTip(null);
        return;
      }
      const time = crosshairTimeString(param.time);
      const p = payloadRef.current?.bars;
      if (!p) {
        setHoverTip(null);
        return;
      }
      const c30 = pickCorr(p.corr30, time);
      const c60 = pickCorr(p.corr60, time);
      const c90 = pickCorr(p.corr90, time);
      const s90 = pickCorr(p.spearman90, time);
      if (c30 == null && c90 == null) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: param.point.x,
        y: param.point.y,
        time,
        ...situationAtPoint(c30, c60, c90, s90),
      });
    };
    chart.subscribeCrosshairMove(onCrosshairMove);
    chartRef.current = chart;

    const disconnect = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height: height || 360 });
    });

    return () => {
      chart.unsubscribeCrosshairMove(onCrosshairMove);
      disconnect();
      chart.remove();
      chartRef.current = null;
      setHoverTip(null);
    };
  }, [payload]);

  const r90 = useMemo(() => regime(latest?.correlation?.d90), [latest]);

  return (
    <section className="bond-dash-panel btc-nasdaq-panel">
      <div className="bond-dash-header btc-nasdaq-header">
        <div className="btc-nasdaq-header-main">
          <h3 className="bond-dash-title">Bitcoin vs Nasdaq — separate asset or leveraged tech?</h3>
          <p className="bond-dash-subtitle">
            {payload?.btc_method_note || 'BTC vs QQQ on Nasdaq trading days only.'}
            {payload?.diagnostics?.aligned_from && payload?.diagnostics?.aligned_to
              ? ` · ${payload.diagnostics.aligned_from} → ${payload.diagnostics.aligned_to} (${payload.count_aligned_days ?? '—'} sessions)`
              : ''}
            . Hover for the regime at that date. Dotted lines: Pearson 30/60/90d log-return
            correlation; dashed purple: Spearman 90d.
          </p>
          <BtcNasdaqHelp />
        </div>
        {latest && (
          <div className="btc-nasdaq-legend">
            <span className="btc-nasdaq-legend-btc">
              {payload?.btc_display_symbol || 'BTC'}{' '}
              {latest.btc_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className="btc-nasdaq-legend-nq">
              {payload?.nasdaq_symbol} {latest.nasdaq_indexed?.toFixed(1) ?? '—'}
            </span>
            <span className={`btc-nasdaq-regime btc-nasdaq-regime--${r90.cssClass}`}>
              {r90.emoji} {r90.label} (90d)
            </span>
          </div>
        )}
      </div>

      {situation && (
        <div className="btc-nasdaq-signal-block">
          <span className={`btc-nasdaq-signal-headline btc-nasdaq-signal-headline--${situation.primary_css_class}`}>
            {situation.headline}
          </span>
          <p className="btc-nasdaq-signal-detail">{situation.detail}</p>
          <p className="btc-nasdaq-signal-tooltip-line">{situation.tooltip_line}</p>
        </div>
      )}

      <div className="stock-bond-toolbar">
        <span className="stock-bond-range-label">Range</span>
        {RANGE_OPTIONS.map((label) => (
          <button
            key={label}
            type="button"
            className={`stock-bond-range-btn${range === label ? ' active' : ''}`}
            onClick={() => setRange(label)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
        <span className="stock-bond-range-label">BTC leg</span>
        {BTC_SOURCE_OPTIONS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`stock-bond-range-btn${btcSource === id ? ' active' : ''}`}
            onClick={() => setBtcSource(id)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
        {btcSource === 'kraken' && (
          <>
            <span className="stock-bond-range-label">Kraken close</span>
            <button
              type="button"
              className={`stock-bond-range-btn${btcMethod === 'aligned_21utc' ? ' active' : ''}`}
              onClick={() => setBtcMethod('aligned_21utc')}
              disabled={loading}
            >
              21:00 UTC
            </button>
            <button
              type="button"
              className={`stock-bond-range-btn${btcMethod === 'daily' ? ' active' : ''}`}
              onClick={() => setBtcMethod('daily')}
              disabled={loading}
            >
              Daily
            </button>
          </>
        )}
      </div>

      {loading && <div className="bond-dash-loading">Loading BTC vs Nasdaq…</div>}
      {error && <div className="bond-dash-error">{error}</div>}

      {payload?.bars && !error && (
        <div className="stock-bond-charts">
          <div className="stock-bond-corr-header">
            <span className="stock-bond-corr-title">Rolling correlation (log returns)</span>
            <div className="stock-bond-corr-legend">
              <span style={{ color: CORR_COLORS[30] }}>30d {fmtSignedCorr(latest?.correlation?.d30)}</span>
              <span style={{ color: CORR_COLORS[60] }}>60d {fmtSignedCorr(latest?.correlation?.d60)}</span>
              <span style={{ color: CORR_COLORS[90] }}>90d {fmtSignedCorr(latest?.correlation?.d90)}</span>
              <span style={{ color: '#6A4C93' }}>
                Sp90 {fmtSignedCorr(latest?.correlation?.spearman90)}
              </span>
              <span>12w {fmtSignedCorr(latest?.correlation?.weekly12)}</span>
            </div>
          </div>
          <div className="stock-bond-chart-wrap">
            <span className="stock-bond-axis-label stock-bond-axis-label--left">Indexed (100)</span>
            <div ref={chartContainerRef} className="stock-bond-chart stock-bond-chart-combined" />
            {hoverTip && (
              <div
                className="stock-bond-chart-tooltip"
                style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
                role="tooltip"
              >
                <div className="stock-bond-chart-tooltip-date">{formatChartDate(hoverTip.time)}</div>
                <div className={`stock-bond-chart-tooltip-regime btc-nasdaq-regime--${hoverTip.primaryCss}`}>
                  {hoverTip.r90?.emoji} {hoverTip.headline}
                </div>
                <p className="stock-bond-chart-tooltip-body">{hoverTip.detail}</p>
              </div>
            )}
            <span className="stock-bond-axis-label stock-bond-axis-label--right">Correlation</span>
          </div>
        </div>
      )}
    </section>
  );
}

export default BtcNasdaqPanel;
